import i18n, { t } from "i18next";
import { Trans } from "react-i18next";
import { type ReactElement } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";

function RoleList() {
  const rawRoles = [
    {
      element: (
        <Trans
          i18nKey="role_musician"
          defaults="<0>musician</0>"
          components={[<span className="text-accent-pink role-highlight" />]}
        />
      ),
      hasN: false,
      textLength: t("role_musician", "<0>musician</0>").replace(/<\/?\d+>/g, "")
        .length,
      last: false,
    },
    {
      element: (
        <Trans
          i18nKey="role_audio_enthusiast"
          defaults="<0>audio</0> enthusiast"
          components={[<span className="text-accent-cyan role-highlight" />]}
        />
      ),
      hasN: true,
      textLength: t("role_audio_enthusiast", "<0>audio</0> enthusiast").replace(
        /<\/?\d+>/g,
        "",
      ).length,
      last: false,
    },
    {
      element: (
        <Trans
          i18nKey={
            new Date() < new Date("2026-04-30")
              ? "role_oakland_student"
              : "role_oakland_graduate"
          }
          defaults={
            new Date() < new Date("2026-04-30")
              ? "<0>Oakland</0> student"
              : "<0>Oakland</0> graduate"
          }
          components={[<span className="text-ou-gold role-highlight" />]}
        />
      ),
      hasN: true,
      textLength: t(
        new Date() < new Date("2026-04-30")
          ? "role_oakland_student"
          : "role_oakland_graduate",
        new Date() < new Date("2026-04-30")
          ? "<0>Oakland</0> student"
          : "<0>Oakland</0> graduate",
      ).replace(/<\/?\d+>/g, "").length,
      last: false,
    },
    {
      element: (
        <Trans
          i18nKey="role_full_stack_developer"
          defaults="full stack <0>developer</0>"
          components={[<span className="text-accent-blue role-highlight" />]}
        />
      ),
      hasN: false,
      textLength: t(
        "role_full_stack_developer",
        "full stack <0>developer</0>",
      ).replace(/<\/?\d+>/g, "").length,
      last: false,
    },
    {
      element: (
        <Trans
          i18nKey="role_open_source_contributor"
          defaults="open-source <0>contributor</0>"
          components={[<span className="text-accent-orange role-highlight" />]}
        />
      ),
      hasN: true,
      textLength: t(
        "role_open_source_contributor",
        "open-source <0>contributor</0>",
      ).replace(/<\/?\d+>/g, "").length,
      last: false,
    },
    {
      element: (
        <span className="text-accent-green role-highlight">
          {t("good_fit", "good fit for your company")}
        </span>
      ),
      hasN: false,
      textLength: t("good_fit", "good fit for your company").length,
      last: true,
    },
  ];

  const roles: [ReactElement, boolean][] = rawRoles
    .sort((a, b) => {
      if (a.last) return 1;
      if (b.last) return -1;
      return a.textLength - b.textLength;
    })
    .map((r) => [r.element, r.hasN]);
  useGSAP(() => {
    document.fonts.ready.then(() => {
      const html_roles = gsap.utils.toArray(".role-text");
      const tl = gsap.timeline({ repeat: 0 });
      tl.delay(2);

      html_roles.forEach((role: any, i) => {
        const previousRoles = html_roles.slice(0, i);

        if (previousRoles.length > 0) {
          tl.to(
            previousRoles,
            {
              y: "+=1.2em",
              opacity: (index) => {
                return (roles.length - (i - index)) / roles.length / 2;
              },
              filter: (index) => `blur(${(i - index) * 0.125}rem)`,
              duration: 0.5,
              ease: "power2.inOut",
            },
            "+=1",
          );

          tl.to(
            ".static-n",
            {
              opacity: roles?.[i]?.[1] ? 1 : 0,
              duration: 0.5,
              ease: "power2.inOut",
            },
            "<",
          );

          tl.fromTo(
            role,
            { y: "-2.4em", opacity: 0, filter: "blur(0.625rem)" },
            {
              y: "0em",
              opacity: 1,
              filter: "blur(0rem)",
              duration: 1,
              ease: "power2.inOut",
            },
            "<",
          );
        } else {
          tl.fromTo(
            role,
            { y: "-1.2em", opacity: 0, filter: "blur(0.625rem)" },
            {
              y: "0em",
              opacity: 1,
              filter: "blur(0rem)",
              duration: 0.5,
              ease: "power2.inOut",
            },
          );

          if (roles?.[i]?.[1]) {
            tl.to(
              ".static-n",
              {
                width: "auto",
                opacity: 1,
                duration: 0.5,
                ease: "power2.inOut",
              },
              "<",
            );
          }
        }
      });
    });
  });
  return (
    <h2
      aria-label={t("role_aria_label", "I'm a musician, audio enthusiast, Oakland graduate, full stack developer, open-source contributor, and a good fit for your company.")}
      className={`${
        i18n.language === "ja"
          ? "text-[5.5vw] sm:text-[2.8rem] lg:text-[4.5rem]"
          : "text-[6vw] sm:text-[2.8rem] lg:text-[4.5rem]"
      } flex relative leading-snug`}
    >
      <span className="shrink-0 flex pr-3 lg:pr-5">
        {t("im_a", "I'm a")}
        <span
          className="static-n inline-block overflow-hidden opacity-0"
          style={{
            display: i18n.language === "ja" ? "none" : undefined,
          }}
        >
          {i18n.language === "ja" ? "" : "n"}
        </span>
      </span>
      <span className="roles-container relative flex-1">
        {roles.map((role, i) => (
          <span key={i} className="role-text absolute left-0 whitespace-nowrap">
            {role[0]}
          </span>
        ))}
      </span>
    </h2>
  );
}

export default RoleList;
